import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  CurrencyAddedSignalRPayload,
  CurrencyRemovedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type CurrencyEventHandlers = {
  onCurrencyAdded?: (
    payload: SignalRReceiveEvent<CurrencyAddedSignalRPayload>
  ) => void
  onCurrencyRemoved?: (
    payload: SignalRReceiveEvent<CurrencyRemovedSignalRPayload>
  ) => void
}

export function useCurrencySignalREvents(handlers?: CurrencyEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToCurrencyAdded(
    cb: (payload: SignalRReceiveEvent<CurrencyAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CurrencyAddedSignalRPayload>(
      signalREvent.Monetary.CurrencyAdded,
      cb,
      options
    )
  }

  function subscribeToCurrencyRemoved(
    cb: (payload: SignalRReceiveEvent<CurrencyRemovedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CurrencyRemovedSignalRPayload>(
      signalREvent.Monetary.CurrencyRemoved,
      cb,
      options
    )
  }

  function subscribeToCurrencyEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onCurrencyAdded) {
        unsubscribeFunctions.push(
          subscribeToCurrencyAdded(handlers.onCurrencyAdded)
        )
      }
      if (handlers.onCurrencyRemoved) {
        unsubscribeFunctions.push(
          subscribeToCurrencyRemoved(handlers.onCurrencyRemoved)
        )
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromCurrencyEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToCurrencyEvents,
    unsubscribeFromCurrencyEvents
  }
}
